import React, { useEffect, useMemo, useState } from 'react';
import { useImmer } from 'use-immer';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { api } from '../../utils/api';
import Bugsnag from "@bugsnag/js";
import { arrayMove } from '@dnd-kit/sortable';
import { userFullName } from '../../utils/userUtils';
import LEAD_FIELDS, { EXPORT_FIELDS, NAME_FIELDS } from '../../utils/leadFields';
import useGlobalParams from '../../hooks/useGlobalParams';
import useStatuses from '../../hooks/useStatuses';
import useCustomFields from '../../hooks/useCustomFields';
import useUsers from '../../hooks/useUsers';

import LeadStatusDropdown from './LeadStatusDropdown';
import Sortable from '../../common/Sortable/Sortable';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import Button from '../../common/Button/Button';
import { DropdownToggle } from '../../common/Dropdown/Dropdown';
import Dropdown from 'react-bootstrap/Dropdown';
import Form from 'react-bootstrap/Form';

import { ReactComponent as CheckMarkIcon } from "../../assets/icons_svg/check-mark-outline.svg";

import '../../common/Dropdown/Dropdown.scss';
import './ExportModal.scss';


const normalizeFileName = string => (
  string.trim().normalize("NFD").replace(/\W+/g, '_')
);

const EXPORT_TYPES = {
  all      : "All leads from this category",
  status   : "Specific statuses",
  selected : "Selected leads",
  filter   : "Filtered leads",
}

export default function ExportModal({ show, onHide, category, selectedLeads, filters }) {
  const { organizationId, categoryId } = useGlobalParams();
  const { data: statuses } = useStatuses({ categoryId });
  const { data: users } = useUsers({ organizationId, categoryId });
  const { data: customFields } = useCustomFields({ categoryId });

  const [downloadLink, setDownloadLink] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [type, setType] = useState('all');
  const [statusIds, setStatusIds] = useState([]);
  const [step, setStep] = useState('leads');
  const [columns, setColumns] = useImmer(null);

  const categoryName = category?.name;

  useEffect(() => {
    if (show) {
      setDownloadLink(false);
      setStatusIds([]);
      setError(false);
      setProcessing(false);
      setStep('leads');

      if (selectedLeads?.size > 0) {
        setType('selected');
      }
      else if (filters?.length > 0) {
        setType('filter');
      }
      else {
        setType('all');
      }
    }
  }, [show, filters, selectedLeads]);

  const onChooseFields = () => {
    setStep('fields');
    setColumns([...EXPORT_FIELDS.map(column => ({ column, visible: true })), ...customFields.map(field => ({ column: field.code, visible: true, label: field.label }))]);
  }

  const fileName = useMemo(() => {
    if (downloadLink) {
      const date = DateTime.now();
      return normalizeFileName(categoryName) + '_' + date.toFormat('yyyy-LL-dd') + '.csv';
    }
  }, [downloadLink, categoryName]);

  const renderFilterText = filter => {
    if (filter.field === 'status_id') {
      return <>
        <span className='field'>{ LEAD_FIELDS[filter.field].label }</span> <span className='match'>{filter.match}</span>  
        { filter.value.map((value, index) => <React.Fragment key={value}><span className='search'>{ statuses.find(status => status.id === value).name }</span> { index < filter.value.length - 1 && ' or ' }</React.Fragment> )}
      </>
    }
    else if (filter.field === 'assigned_user_id') {
      return <>
        <span className='field'>{ LEAD_FIELDS[filter.field].label }</span> <span className='match'>{filter.match}</span>  
        <span className='search'>{ userFullName(users.find(status => status.id === filter.value)) }</span>
      </>
    }
    else {
      return <>
        <span className='field'>{ LEAD_FIELDS[filter.field].label }</span> <span className='match'>{filter.match}</span> <span className='search'>{filter.value}</span>
      </>
    }
  }

  const showField = key => {
    if (key === 'selected' && (!selectedLeads || selectedLeads.size === 0)) {
      return false;
    }
    else if (key === 'filter' && (!filters || filters.length === 0)) {
      return false;
    }

    return true;
  }

  const normalizeFieldName = field => {
    if (field === 'status_id') {
      return 'status_name';
    }
    else if (field === 'assigned_user_id') {
      return 'assigned_user_name';
    }

    return field;
  }

  const onExport = async () => {
    setProcessing(true);
    setStep('export');

    const params = new URLSearchParams({ category_id: categoryId });

    if (type === 'filter') {
      filters.forEach(filter => {
        params.append(filter.field + (filter.match === 'contains' ? '_contains' : ''), filter.value);
      });
    }

    if (type === 'status') {
      params.append('status_id', statusIds.join(','));
    }

    // test
    if (step === 'fields') {
      console.log('columns:', columns)
      params.append('fields', columns.filter(item => item.visible).map(item => normalizeFieldName(item.column)).join(','));
    }

    try {
      const { data } = await api.post(`/leads/export/?${params.toString()}`, {
        lead_ids: type === 'selected' ? Array.from(selectedLeads) : undefined,
      });
  
      setDownloadLink('data:text/plain;charset=utf-8,' + encodeURIComponent(data));
    }
    catch (e) {
      Bugsnag.notify(e);
      setError(true);
    }
    finally {
      setProcessing(false);
    }
  }

  const onChangeOrder = (activeIndex, overIndex) => {
    const newOrder = arrayMove(columns, activeIndex, overIndex);
    setColumns(newOrder);
  }

  const onChangeVisibility = (e, column) => {
    setColumns(draft => {
      const index = draft.findIndex(item => item.column === column);
      draft[index].visible = e.target.checked;
    });
  }

  const onSelectBulk = visibility => {
    setColumns(draft => {
      draft.forEach(item => {
        item.visible = visibility;
      })
    });
  }

  const renderField = ({ column, visible, label }) => {
    return (
      <Sortable.Item id={column}>
        <div className='sortable-column' key={column}>
          <div className="" ref={null}><Sortable.DragHandle /></div>
          <Form.Check checked={visible} onChange={e => onChangeVisibility(e, column)} />
          <div className='column-name'>{ NAME_FIELDS[column] || LEAD_FIELDS[column]?.label || label }</div>
          { label && <div className='custom-field-pill'>Custom field</div> }
        </div>
      </Sortable.Item>
    );
  }

  const renderSelectionCount = () => {
    const visibleColumns = columns.filter(column => column.visible).length;

    if (visibleColumns === EXPORT_FIELDS.length + customFields.length) {
      return <strong>All fields selected</strong>;
    }
    else if (visibleColumns === 0) {
      return 'Select at least one field to export';
    }
    else {
      // TODO -> language -> plural
      return <><strong>{ visibleColumns }</strong> fields selected</>
    }
  }

  const renderContent = () => {
    switch (step) {
      case 'fields':
        return <>
          <p className='selection-desc'>Select and reorder the fields that you will like to export:</p>
          <div className='select-bulk'>
            <div className='select-label'>Select</div>
            <button onClick={() => onSelectBulk(true)}>All</button>
            <button onClick={() => onSelectBulk(false)}>None</button>
          </div>
          <Sortable items={columns.map(row => ({ id: row.column, ...row }))} renderItem={renderField} onChange={onChangeOrder} />
          <div className='selection-count'>{ renderSelectionCount() }</div>
        </>

      case 'export':
        if (processing) {
          return (
            <>
              <div className='spinner'><Spinner /></div>
              <p>Preparing CSV export...</p>
            </>
          );
        }
        else if (error) {
          return (
            <>
              <h2 className='failed'>Export failed</h2>
              <p className='export-ready'>There was a problem with your export. Try again or <a href="#WIP">contact us</a> if the error persists</p>
              <Button variant='link' onClick={onHide}>Close</Button>
            </>
          );
        }
        else if (downloadLink) {
          return (
            <>
            <div className='checkmark'><CheckMarkIcon /></div>
            <p className='export-ready'>Your export file is ready!</p>
            <p><Button as='a' href={downloadLink} variant='primary' download={fileName}>Download CSV</Button></p>
            <Button variant='link' onClick={onHide}>Close</Button>
            </>
          );
        }
        break;

      case 'leads':
      default:
        return (
          <>
            <div className='select-leads'>
              <div>Select leads to export:</div>
              <Dropdown className='__dropdown'>
                <Dropdown.Toggle as={DropdownToggle}>
                  <div className='label'>{ EXPORT_TYPES[type] }</div>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  { Object.entries(EXPORT_TYPES).map(([key, value]) => (
                    showField(key) && <Dropdown.Item key={key} onClick={() => setType(key)}>{ value }</Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>

            { type === 'all' && (
              <div className='export-details centered'>
                <div><strong>{category?.lead_count}</strong> leads will be exported in CSV format</div>
              </div>
            )}

            { type === 'selected' && (
              <div className='export-details centered'>
                <div><strong>{selectedLeads.size}</strong> selected leads will be exported in CSV format</div>
              </div>
            )}

            { type === 'status' && (
              <>
                <div className='export-status-label'>Select the statuses to export</div>
                <LeadStatusDropdown type='large' multiple statuses={statuses}  onChange={ids => setStatusIds(ids)} selectedIds={statusIds} placeholder='Select a status' />
              </>
            )}

            { type === 'filter' && (
              <div className='export-details'>
                <div>
                  <p>Leads that follow the selected filter will be exported:</p>
                  <ul>
                    { filters.map(filter => <li>{ renderFilterText(filter) }</li> )}
                  </ul>
                </div>
              </div>
            )}
          </>
        );
    }
  }

  return (
    <Modal className='__modal __export-modal' show={show} onHide={step !== 'export' && onHide} backdrop='static' centered>
      <Modal.Header closeButton={step !== 'export'}><h2>Export Leads</h2></Modal.Header>
      <Modal.Body className={classNames({ centered: step === 'export' })}>
        { renderContent() }
      </Modal.Body>
      { step !== 'export' && (
        <Modal.Footer>
          { step === 'leads' 
          ? <div className='fields-to-export'><Button className='link' onClick={onChooseFields}>Select fields to export</Button></div>
          : <div className='fields-to-export'><Button className='link' onClick={() => setStep('leads')}>Cancel</Button></div>
          }
          <Button className='cancel' onClick={onHide}>Close</Button>
          <Button className='confirm' onClick={onExport} disabled={(type === 'status' && statusIds.length === 0) || (step === 'fields' && columns.filter(column => column.visible).length === 0)}>Export</Button>
        </Modal.Footer>
      )}
    </Modal>
  );
}
